import { useState, useCallback, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUpdateTask } from '@/hooks/useTasks';
import { useColumnsQuery } from '@/hooks/useColumns';
import { TaskScheduling } from './TaskScheduling';

interface Task {
  id: string;
  sheetId: string;
  name: string;
  description: string | null;
  startDate: string | null;
  finishDate: string | null;
  duration: number;
  durationUnit: string;
  percentComplete: number;
  status: string | null;
  priority: string | number;
  isCritical: boolean;
  customFields: Record<string, unknown>;
}

interface TaskDetailsProps {
  task: Task;
  sheetId: string;
}

const PRIORITY_OPTIONS = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low' },
];

const STATUS_OPTIONS = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

type PriorityValue = 'low' | 'normal' | 'high' | 'urgent';

function normalizePriority(priority: string | number): PriorityValue {
  if (priority === 'low' || priority === 'normal' || priority === 'high' || priority === 'urgent') {
    return priority;
  }

  if (typeof priority === 'number') {
    if (priority <= 100) return 'urgent';
    if (priority <= 250) return 'high';
    if (priority <= 500) return 'normal';
    return 'low';
  }

  return 'normal';
}

export function TaskDetails({ task, sheetId }: TaskDetailsProps) {
  const updateTask = useUpdateTask(sheetId);
  const { data: columns } = useColumnsQuery(sheetId);

  // Local state for optimistic updates
  const [name, setName] = useState(task.name);
  const [description, setDescription] = useState(task.description || '');
  const [percentComplete, setPercentComplete] = useState(task.percentComplete);
  const [duration, setDuration] = useState(task.duration.toString());

  // Sync local state when task changes
  useEffect(() => {
    setName(task.name);
    setDescription(task.description || '');
    setPercentComplete(task.percentComplete);
    setDuration(task.duration.toString());
  }, [task.id, task.name, task.description, task.percentComplete, task.duration]);

  const handleUpdate = useCallback(
    (field: string, value: unknown) => {
      updateTask.mutate({
        taskId: task.id,
        updates: { [field]: value },
      });
    },
    [task.id, updateTask]
  );

  const handleNameBlur = useCallback(() => {
    if (name !== task.name && name.trim()) {
      handleUpdate('name', name.trim());
    }
  }, [name, task.name, handleUpdate]);

  const handleDescriptionBlur = useCallback(() => {
    if (description !== (task.description || '')) {
      handleUpdate('description', description || null);
    }
  }, [description, task.description, handleUpdate]);

  const handleDurationBlur = useCallback(() => {
    const durationNum = parseFloat(duration);
    if (!isNaN(durationNum) && durationNum !== task.duration) {
      handleUpdate('duration', durationNum);
    }
  }, [duration, task.duration, handleUpdate]);

  const handlePercentCompleteChange = useCallback(
    (value: number[]) => {
      const newValue = value[0];
      setPercentComplete(newValue);
    },
    []
  );

  const handlePercentCompleteCommit = useCallback(
    (value: number[]) => {
      const newValue = value[0];
      if (newValue !== task.percentComplete) {
        handleUpdate('percentComplete', newValue);
      }
    },
    [task.percentComplete, handleUpdate]
  );

  const handleStartDateChange = useCallback(
    (date: Date | undefined) => {
      handleUpdate('startDate', date ? date.toISOString().split('T')[0] : null);
    },
    [handleUpdate]
  );

  const handleEndDateChange = useCallback(
    (date: Date | undefined) => {
      handleUpdate('endDate', date ? date.toISOString().split('T')[0] : null);
    },
    [handleUpdate]
  );

  const handlePriorityChange = useCallback(
    (value: string) => {
      handleUpdate('priority', value as PriorityValue);
    },
    [handleUpdate]
  );

  const handleStatusChange = useCallback(
    (value: string) => {
      handleUpdate('status', value);
    },
    [handleUpdate]
  );

  const handleCustomFieldChange = useCallback(
    (fieldId: string, value: unknown) => {
      const updatedCustomFields = {
        ...task.customFields,
        [fieldId]: value,
      };
      handleUpdate('customFields', updatedCustomFields);
    },
    [task.customFields, handleUpdate]
  );

  // Filter columns that are custom fields (not built-in system columns)
  // Built-in column types that map to task fields
  const builtInColumnTypes = ['text', 'date', 'duration', 'percent', 'picklist', 'number'];
  const customFieldColumns = columns?.filter(
    (col) =>
      !col.isPrimary && // Not the primary name column
      !builtInColumnTypes.includes(col.columnType) // Not a built-in type that we handle above
  );

  return (
    <div className="space-y-6">
      {/* Task Name */}
      <div className="space-y-2">
        <Label htmlFor="task-name">Task Name</Label>
        <Input
          id="task-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={handleNameBlur}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.currentTarget.blur();
            }
          }}
          className="text-lg font-medium"
          placeholder="Enter task name"
        />
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="task-description">Description</Label>
        <Textarea
          id="task-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onBlur={handleDescriptionBlur}
          placeholder="Add a description..."
          rows={4}
        />
      </div>

      {/* Dates and Duration */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Start Date</Label>
          <DatePicker
            value={task.startDate ? new Date(task.startDate) : null}
            onChange={handleStartDateChange}
            placeholder="Select start"
          />
        </div>

        <div className="space-y-2">
          <Label>End Date</Label>
          <DatePicker
            value={task.finishDate ? new Date(task.finishDate) : null}
            onChange={handleEndDateChange}
            placeholder="Select end"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="task-duration">Duration</Label>
          <div className="flex items-center gap-2">
            <Input
              id="task-duration"
              type="number"
              min="0"
              step="0.5"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              onBlur={handleDurationBlur}
              className="flex-1"
            />
            <span className="text-sm text-muted-foreground">
              {task.durationUnit === 'd' ? 'days' : task.durationUnit}
            </span>
          </div>
        </div>
      </div>

      {/* Percent Complete */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Progress</Label>
          <span className="text-sm font-medium">{percentComplete}%</span>
        </div>
        <Slider
          value={[percentComplete]}
          onValueChange={handlePercentCompleteChange}
          onValueCommit={handlePercentCompleteCommit}
          max={100}
          step={5}
          className="py-2"
        />
      </div>

      {/* Priority and Status */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Priority</Label>
          <Select
            value={normalizePriority(task.priority)}
            onValueChange={handlePriorityChange}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select priority" />
            </SelectTrigger>
            <SelectContent>
              {PRIORITY_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Status</Label>
          <Select
            value={task.status || 'not_started'}
            onValueChange={handleStatusChange}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Scheduling Section */}
      <TaskScheduling task={task} sheetId={sheetId} />

      {/* Custom Fields */}
      {customFieldColumns && customFieldColumns.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
            Custom Fields
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {customFieldColumns.map((column) => (
              <CustomFieldInput
                key={column.id}
                column={column}
                value={task.customFields[column.id]}
                onChange={(value) => handleCustomFieldChange(column.id, value)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Custom field input component
interface Column {
  id: string;
  name: string;
  columnType: string;
  options?: {
    choices?: Array<{ value: string; label: string; color?: string }>;
  };
}

interface CustomFieldInputProps {
  column: Column;
  value: unknown;
  onChange: (value: unknown) => void;
}

function CustomFieldInput({ column, value, onChange }: CustomFieldInputProps) {
  const [localValue, setLocalValue] = useState(String(value || ''));

  useEffect(() => {
    setLocalValue(String(value || ''));
  }, [value]);

  const handleBlur = () => {
    if (localValue !== String(value || '')) {
      onChange(localValue || null);
    }
  };

  switch (column.columnType) {
    case 'dropdown':
    case 'picklist':
      return (
        <div className="space-y-2">
          <Label>{column.name}</Label>
          <Select
            value={String(value || '')}
            onValueChange={onChange as (value: string) => void}
          >
            <SelectTrigger>
              <SelectValue placeholder={`Select ${column.name.toLowerCase()}`} />
            </SelectTrigger>
            <SelectContent>
              {column.options?.choices?.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );

    case 'number':
      return (
        <div className="space-y-2">
          <Label>{column.name}</Label>
          <Input
            type="number"
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={() => {
              const numValue = parseFloat(localValue);
              if (!isNaN(numValue) && numValue !== value) {
                onChange(numValue);
              }
            }}
          />
        </div>
      );

    case 'date':
      return (
        <div className="space-y-2">
          <Label>{column.name}</Label>
          <DatePicker
            value={value ? new Date(value as string) : null}
            onChange={(date) =>
              onChange(date ? date.toISOString().split('T')[0] : null)
            }
            placeholder={`Select ${column.name.toLowerCase()}`}
          />
        </div>
      );

    case 'checkbox':
      return (
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id={`custom-${column.id}`}
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
          />
          <Label htmlFor={`custom-${column.id}`}>{column.name}</Label>
        </div>
      );

    default:
      return (
        <div className="space-y-2">
          <Label>{column.name}</Label>
          <Input
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={handleBlur}
            placeholder={`Enter ${column.name.toLowerCase()}`}
          />
        </div>
      );
  }
}

export default TaskDetails;
